// ==========================================================================
// screens/Home.tsx — the family's front page (FF-026 design pass, 9 Oct 2026)
//   Hero: today's date large, a greeting, three quick actions.
//   Left:  today and tomorrow from the calendar, then the week at a glance.
//   Right: the vehicles and WFH figures for this financial year, recent changes.
// Money cards arrive with the Money build and take the top of the right column.
// FF-058 (10 Oct, James: "refine home screen now and make the logo more
// prominent"): a banner with the crest large in the theme colour and the
// wordmark, the date, and today at a glance (on today, dinner, to do, due);
// six quick actions.
// ==========================================================================
import { Link, useNavigate } from "react-router-dom";
import { addDays, addWeeks, format, parseISO, startOfWeek } from "date-fns";
import { Baby, CalendarClock, CalendarPlus, Car, ArrowUpRight, ChefHat, Laptop, MapPin, ShoppingBasket, type LucideIcon } from "lucide-react";
import { useState } from "react";
import EventSheet from "@/components/EventSheet";
import Emblem from "@/components/Emblem";
import { useMeals, useRecipes } from "@/lib/kitchen";
import { useTasks } from "@/lib/tasks";
import { billPending, useReceipts } from "@/lib/receipts";
import { daysTo, useReminders } from "@/lib/reminders";
import { Card } from "@/components/ui";
import HomeGarden from "@/components/HomeGarden";
import { useMe, usePeople } from "@/lib/household";
import TasksWidget from "@/components/TasksWidget";
import DueSoon from "@/components/DueSoon";
import NetWorth from "@/components/NetWorth";
import SpendWidget from "@/components/SpendWidget";
import MiniBars from "@/components/MiniBars";
import { expand, mapsLink, occTitle, tokenColour, useCalendarExtras, useEvents, ymd, type Occurrence } from "@/lib/calendar";
import { fyOf, fyRange, useTrips, useUsePeriods, useVehicles, vehicleStats, typeOf } from "@/lib/vehicles";
import { useWfhDays } from "@/lib/wfh";
import "./Home.css";

const TZ = "Australia/Brisbane";
const clock = (d: Date) => d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", timeZone: TZ }).replace(/\s/g, "").toLowerCase();

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

/** Existing actions, grouped as everyday tools. */
function Action({ to, onClick, icon: Icon, label, sub }: { to?: string; onClick?: () => void; icon: LucideIcon; label: string; sub: string }) {
  const inner = <><span className="home-tool-icon"><Icon size={20} /></span><span><strong>{label}</strong><small>{sub}</small></span><ArrowUpRight size={15} className="home-tool-arrow" /></>;
  return onClick ? <button type="button" onClick={onClick} className="home-tool">{inner}</button> : <Link to={to!} className="home-tool">{inner}</Link>;
}

export default function Home() {
  const nav = useNavigate();
  // FF-062: "Add an event" opens the add sheet right here, no trip to the calendar.
  const [adding, setAdding] = useState(false);
  const me = useMe(); const people = usePeople();
  const events = useEvents(); const extras = useCalendarExtras();
  const allVehicles = useVehicles(); const trips = useTrips(); const periods = useUsePeriods();
  // Only road vehicles we still own log trips (032).
  const vehicles = { ...allVehicles, data: allVehicles.data?.filter((v) => !v.assets.disposed_on && typeOf(v.vehicle_type).trips) };
  const fy = fyOf(new Date()); const r = fyRange(fy);
  const wfhYear = useWfhDays(me?.id, r.from, r.to);
  const ppl = people.data ?? [];
  const todayYmd = ymd(new Date());
  const meals = useMeals(todayYmd, todayYmd); const recipes = useRecipes();
  const tasks = useTasks(); const receipts = useReceipts(); const reminders = useReminders();
  const dinner = (meals.data ?? []).find((m) => m.slot === "dinner");
  const dinnerName = dinner ? (dinner.recipe_id ? recipes.data?.find((r) => r.id === dinner.recipe_id)?.title : dinner.label) ?? "Planned" : null;
  const openTasks = (tasks.data ?? []).filter((t) => !t.done_at && t.kind === "todo").length;
  const dueWeek = (receipts.data ?? []).filter((r) => billPending(r) && r.due_on && daysTo(r.due_on) <= 7).length
    + (reminders.data ?? []).filter((r) => !r.done_on && daysTo(r.due_on) <= 7).length;

  const today = ymd(new Date()); const tomorrow = ymd(addDays(new Date(), 1));
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const next = expand([...(events.data ?? []), ...(extras.data ?? [])], today, ymd(addDays(weekStart, 13)));
  const soon = next.filter((o) => o.date === today || o.date === tomorrow);
  const week = Array.from({ length: 7 }, (_, i) => ymd(addDays(weekStart, i)));
  const colour = (o: Occurrence) => o.event.colour ?? ppl.find((p) => p.id === o.people[0])?.colour ?? "text-muted";

  // Last 12 weeks of km, all vehicles: work vs private.
  const kmWeeks = Array.from({ length: 12 }, (_, i) => {
    const from = addWeeks(weekStart, i - 11); const to = addDays(from, 6);
    const inW = (trips.data ?? []).filter((t) => t.trip_date >= ymd(from) && t.trip_date <= ymd(to));
    const use = (t: (typeof inW)[number]) => t.reviewed_use ?? t.proposed_use;
    return { label: format(from, "d/M"), value: inW.filter((t) => use(t) === "work").reduce((a, t) => a + t.distance_km, 0), value2: inW.filter((t) => use(t) !== "work").reduce((a, t) => a + t.distance_km, 0) };
  });
  const wfhMonths = Array.from({ length: 12 }, (_, i) => {
    const m = new Date(Number(fy.slice(0, 4)), 6 + i, 1); const key = format(m, "yyyy-MM");
    return { label: format(m, "MMM")[0], value: (wfhYear.data ?? []).filter((d) => d.work_date.startsWith(key)).reduce((a, d) => a + d.hours, 0) };
  });
  const thisWeekWfh = (wfhYear.data ?? []).filter((d) => d.work_date >= week[0] && d.work_date <= week[6]).length;
  const wfhHours = (wfhYear.data ?? []).reduce((s, d) => s + d.hours, 0);

  return (
    <div className="home-daybook home-almanac">
      <header className="home-family-day">
        <div className="home-family-copy">
          <div className="home-family-wordmark"><Emblem className="home-family-emblem" /><span>O’Sullivan <small>our family, every day</small></span></div>
          <p className="home-greeting">{greeting()}{me ? `, ${me.display_name}` : ""}.</p>
          <h1>Hello, <em>{format(new Date(), "EEEE")}.</em></h1>
          <p className="home-date">{format(new Date(), "d MMMM yyyy")} <span aria-hidden="true">·</span> A little planning. More time together.</p>
          <div className="home-family-footer">
            <div className="home-family-people" aria-label="Our family">{ppl.map((p) => <span key={p.id} title={p.display_name} style={{ borderColor: tokenColour(p.colour) }}>{p.display_name[0]}</span>)}</div>
            <a href="#home-tasks" className="home-family-status">{tasks.isError ? "To-dos unavailable" : tasks.isPending ? "Loading to-dos…" : openTasks ? `${openTasks} little things to do` : "The to-do list is clear"}<ArrowUpRight size={15} /></a>
          </div>
        </div>
        <div className="home-family-picture"><HomeGarden /><span className="home-art-caption" aria-hidden="true">a place for us</span></div>
      </header>

      <div className="home-life-grid">
        <section className="home-agenda" aria-labelledby="home-agenda-title">
          <div className="home-section-heading"><div><p className="home-eyebrow">Make room for the good things</p><h2 id="home-agenda-title">The next couple of days</h2></div><Link to="/calendar" className="home-text-link">Calendar <ArrowUpRight size={16} /></Link></div>
          <div className="home-agenda-body">
            {events.isError || extras.isError ? <p role="alert" className="home-empty">Your plans couldn't be loaded. Open Calendar to check them.</p> : events.isPending || extras.isPending ? <p role="status" className="home-empty">Gathering your plans…</p> : [today, tomorrow].map((day) => {
              const list = soon.filter((o) => o.date === day);
              return <div key={day} className={day === today ? "home-agenda-day is-today" : "home-agenda-day"}>
                <div className="home-agenda-date"><span>{day === today ? "Today" : "Tomorrow"}</span><strong>{format(parseISO(day), "d")}</strong><small>{format(parseISO(day), "MMM")}</small></div>
                <div className="home-agenda-events">
                  {list.length === 0 ? <div className="home-free-time"><h3>{day === today ? "A little breathing room" : "Nothing planned yet"}</h3><p>{day === today ? "No events in the calendar today." : "Tomorrow is yours to fill."}</p><button type="button" onClick={() => nav("/calendar")} className="home-text-link">Open calendar <ArrowUpRight size={15} /></button></div> : <ul>{list.map((o) => {
                    const who = o.people.map((id) => ppl.find((p) => p.id === id)?.display_name).filter(Boolean).join(", ");
                    return <li key={o.key} className="home-event">
                      <span className="home-event-mark" style={{ background: tokenColour(colour(o)) }} aria-hidden="true" />
                      <div><p className="home-event-time">{o.start ? `${clock(o.start)}${o.end ? ` – ${clock(o.end)}` : ""}` : o.part ? "All day · continues" : "All day"}</p><h3>{occTitle(o)}</h3>{(who || !o.event.virtual) && <p className="home-event-people">{who || "Family"}</p>}</div>
                      {o.event.location && <a href={mapsLink(o.event.location)} target="_blank" rel="noreferrer" aria-label={`Map for ${occTitle(o)}`} className="home-map"><MapPin size={18} /></a>}
                    </li>;
                  })}</ul>}
                </div>
              </div>;
            })}
          </div>
          <Link to="/kitchen/plan" className="home-dinner"><span className="home-dinner-icon"><ChefHat size={23} /></span><span><small>At the family table tonight</small><strong>{meals.isError || recipes.isError ? "Meal plan unavailable" : meals.isPending || recipes.isPending ? "Loading dinner…" : dinnerName ?? "What shall we have?"}</strong></span><ArrowUpRight size={18} /></Link>
          <div className="home-week" aria-label="This week">{week.map((d) => {
            const list = next.filter((o) => o.date === d); const isToday = d === today;
            return <button key={d} type="button" aria-label={`${format(parseISO(d), "EEEE d MMMM")}, ${list.length} events. Open calendar`} aria-current={isToday ? "date" : undefined} onClick={() => nav("/calendar")}><span>{format(parseISO(d), "EEE")}</span><strong>{format(parseISO(d), "d")}</strong><i aria-hidden="true" className={list.length ? "has-events" : ""} /></button>;
          })}</div>
        </section>

        <div id="home-tasks" className="home-notepad"><span className="home-notepad-tape" aria-hidden="true" /><p className="home-eyebrow">A shared list. A lighter load.</p><TasksWidget /></div>

        <nav aria-label="Everyday shortcuts" className="home-tools">
          <div className="home-section-heading"><div><p className="home-eyebrow">The everyday bits</p><h2>A little help at hand</h2></div><span className="home-handwritten" aria-hidden="true">less admin, more living</span></div>
          <div className="home-tools-grid">
            <Action onClick={() => setAdding(true)} icon={CalendarPlus} label="Add an event" sub="Quick add" />
            <Action to="/kitchen/list" icon={ShoppingBasket} label="Shopping" sub="The shared list" />
            <Action to="/journal" icon={Baby} label="Journal" sub="Keep a little memory" />
            <Action to="/vehicles" icon={Car} label="Log a trip" sub={vehicles.isError ? "Vehicles unavailable" : vehicles.data?.length ? vehicles.data.map((v) => v.assets.name).join(", ") : "Add a vehicle"} />
            <Action to="/wfh" icon={Laptop} label="WFH today" sub={`${thisWeekWfh} day${thisWeekWfh === 1 ? "" : "s"} this week`} />
            <Action to="/bills" icon={CalendarClock} label="Bills" sub={dueWeek ? `${dueWeek} due this week` : "Bills and reminders"} />
          </div>
        </nav>
        <div className="home-due"><DueSoon /></div>
      </div>

      <section className="home-financial-chapter" aria-label="Family finances">
        <div className="home-section-heading"><div><p className="home-eyebrow">Looking after our tomorrow</p><h2>The bigger picture</h2></div><Link to="/money/dashboard" className="home-text-link">Money <ArrowUpRight size={16} /></Link></div>
        <div className="home-finances"><NetWorth /></div>
        <div className="home-spending"><SpendWidget /></div>
      </section>
      <section className="home-life-admin" aria-label="Trips and working from home"><div className="home-section-heading"><div><p className="home-eyebrow">Quietly keeping track</p><h2>Life, logged</h2></div><span className="home-period">{fy}</span></div><div className="home-logs">
        <Card className="home-log-card">
          <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h2 className="text-[17px] font-semibold">Kilometres, last 12 weeks</h2>
              <p className="mt-0.5 flex gap-4 text-xs text-ink-muted">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: tokenColour("chart-1") }} />work</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: tokenColour("chart-1", 0.3) }} />private</span>
              </p>
            </div>
            <Link to="/vehicles" className="text-sm font-semibold text-primary">Vehicles</Link>
          </div>
          <MiniBars data={kmWeeks} colour={tokenColour("chart-1")} colour2={tokenColour("chart-1", 0.3)} unit="km" />
          <div className="mt-5 grid grid-cols-2 gap-4 border-t border-line pt-4 sm:grid-cols-3">
            {vehicles.data?.map((v) => {
              const st = vehicleStats(v.asset_id, trips.data ?? [], periods.data ?? [], fy);
              return (
                <Link key={v.asset_id} to={`/vehicles/${v.asset_id}`} className="block">
                  <div className="num text-2xl font-semibold text-primary">{st.workKm.toLocaleString("en-AU")}</div>
                  <div className="text-xs text-ink-muted">{v.assets.name} work km · {fy}{st.toReview > 0 ? ` · ${st.toReview} to review` : ""}</div>
                </Link>
              );
            })}
          </div>
        </Card>

        <Card className="home-log-card">
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="text-[17px] font-semibold">Working from home</h2>
            <Link to="/wfh" className="text-sm font-semibold text-primary">WFH</Link>
          </div>
          <MiniBars data={wfhMonths} colour={tokenColour("chart-4")} unit="hours" />
          <div className="mt-5 grid grid-cols-2 gap-4 border-t border-line pt-4">
            <div><div className="num text-2xl font-semibold" style={{ color: tokenColour("chart-4") }}>{Math.round(wfhHours * 10) / 10}</div><div className="text-xs text-ink-muted">hours · {fy}</div></div>
            <div><div className="num text-2xl font-semibold">{thisWeekWfh}</div><div className="text-xs text-ink-muted">days this week</div></div>
          </div>
        </Card>
      </div>
      </section>
      {adding && <EventSheet date={todayYmd} onClose={() => setAdding(false)} />}
    </div>
  );
}
